import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, AuthCard } from '../layout';

interface ActView {
  number: string; company: string; party: string; side: 'customer' | 'supplier'; from: string; to: string; opening: number;
  rows: { date: string; document: string; debit: number; credit: number }[]; debit: number; credit: number; closing: number; result: string;
  status: 'sent' | 'confirmed' | 'disputed'; responderName: string | null; respondedAt: string | null;
}

/**
 * The page the reconciliation e-mail opens: the act as sent, and the other party confirms the balance or
 * says what differs (with a button; mail scanners that open links decide nothing).
 */
export function ActConfirmPage({ token }: { token: string }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [disagree, setDisagree] = useState(false);
  const lookup = useQuery({ queryKey: ['act-confirm', token], enabled: Boolean(token), retry: false,
    queryFn: () => api.post<{ act: ActView }>('api/v1/act-confirm/lookup', { token }).then((r) => r.act) });
  const answer = useMutation({
    mutationFn: (decision: 'confirmed' | 'disputed') => api.post<{ decision: string }>('api/v1/act-confirm', { token, decision, name, note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['act-confirm', token] }),
  });
  const download = async () => {
    const r = await fetch('api/v1/act-confirm/pdf', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'x-fsp-request': '1' }, body: JSON.stringify({ token }) });
    if (!r.ok) return;
    const url = URL.createObjectURL(await r.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = `suderinimo-aktas-${lookup.data?.to ?? ''}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const a = lookup.data;
  const submit = (e: FormEvent) => { e.preventDefault(); answer.mutate(disagree ? 'disputed' : 'confirmed'); };
  return (
    <AuthCard wide eyebrow="FakturaSmart Pro" title={t('Tarpusavio atsiskaitymų suderinimo aktas')}
      intro={a ? t('{company} prašo patvirtinti tarpusavio atsiskaitymų likutį {date} dienai.', { company: a.company, date: a.to }) : ''}>
      {lookup.isError ? <Alert kind="error">{errorText(t, lookup.error)}</Alert> : null}
      {a ? <>
        <div className="table-wrap">
          <table className="invoice-table">
            <thead><tr><th>{t('Data')}</th><th>{t('Dokumentas')}</th><th className="num">{t('Debetas')}</th><th className="num">{t('Kreditas')}</th></tr></thead>
            <tbody>
              <tr className="recon-sum"><td>{a.from}</td><td>{t('Likutis laikotarpio pradžioje')}</td><td className="num" colSpan={2}>{money(a.opening)}</td></tr>
              {a.rows.map((r, i) => <tr key={i}><td>{r.date}</td><td>{r.document}</td><td className="num">{r.debit ? money(r.debit) : ''}</td><td className="num">{r.credit ? money(r.credit) : ''}</td></tr>)}
              <tr className="recon-sum"><td>{a.to}</td><td>{t('Likutis laikotarpio pabaigoje')}</td><td className="num" colSpan={2}>{money(a.closing)}</td></tr>
            </tbody>
          </table>
        </div>
        <p className="recon-result">{a.result}</p>
        <button type="button" className="text-link" onClick={() => void download()}>{t('Atsisiųsti PDF')} ({a.number})</button>
        {a.status !== 'sent' ? (
          <Alert kind={a.status === 'confirmed' ? 'success' : 'error'}>
            {a.status === 'confirmed' ? t('Likutis patvirtintas.') : t('Pažymėta, kad nesutinkate su likučiu.')}{a.responderName ? ` (${a.responderName})` : ''}
          </Alert>
        ) : (
          <form onSubmit={submit}>
            <label><span>{t('Jūsų vardas, pavardė ir pareigos')}</span><input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={120} /></label>
            <label className="recurring-check"><input type="checkbox" checked={disagree} onChange={(e) => setDisagree(e.target.checked)} /> {t('Nesutinku su likučiu')}</label>
            {disagree ? <label><span>{t('Kas nesutampa? (pvz. jūsų duomenimis likutis, trūkstami dokumentai)')}</span><textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} required maxLength={2000} /></label> : null}
            {answer.isError ? <Alert kind="error">{errorText(t, answer.error)}</Alert> : null}
            <div className="form-actions">
              <button type="submit" className="button button--primary" disabled={answer.isPending}>{disagree ? t('Siųsti nesutapimus') : t('Patvirtinu likutį')}</button>
            </div>
          </form>
        )}
      </> : null}
    </AuthCard>
  );
}
